import React, { useEffect, useState } from "react";
import { api, fmtErr, kg, num, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateDMY from "@/components/DateDMY";
import { toast } from "sonner";
import { Undo2, Ban } from "lucide-react";

const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

function Stat({ label, qty, weight, testid }) {
  return (
    <div className="rounded-md border bg-slate-50 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-mono-num font-semibold text-slate-900">{num(qty, 2)} <span className="text-slate-400">·</span> {kg(weight)}</div>
    </div>
  );
}

function ReturnHistory({ returns, canEdit, onCancel }) {
  if (!returns.length) return <div className="text-sm text-slate-500" data-testid="returns-history-empty">No returns recorded.</div>;
  return (
    <div className="space-y-2" data-testid="returns-history">
      {returns.map((r, i) => {
        const cancelled = r.status === "cancelled";
        return (
          <div key={r.id} className={`rounded-md border p-3 text-sm ${cancelled ? "bg-slate-50 opacity-70" : "bg-white"}`} data-testid={`return-history-${i}`}>
            <div className="flex justify-between gap-2 flex-wrap">
              <div className={cancelled ? "line-through" : ""}>
                <b>{r.return_type}</b> · <span className="font-mono-num">{fmtDate(r.return_date)}</span> · Qty <b className="font-mono-num">{num(r.quantity, 2)}</b> · <span className="font-mono-num">{kg(r.weight_kg)}</span>
              </div>
              {canEdit && !cancelled && (
                <Button size="sm" variant="outline" onClick={() => onCancel(r)} data-testid={`return-cancel-btn-${i}`}><Ban className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
              )}
            </div>
            {r.reason && <div className="text-xs text-slate-600 mt-1">Reason: {r.reason}</div>}
            <div className="text-[11px] text-slate-500 mt-1">By {r.created_by_name} · {new Date(r.created_at).toLocaleString()}</div>
            {cancelled && <div className="text-[11px] text-rose-600 mt-1">Cancelled by {r.cancelled_by_name}: {r.cancel_reason}</div>}
          </div>
        );
      })}
    </div>
  );
}

function ReturnForm({ dispatch, onSaved }) {
  const remQ = dispatch.net_qty;
  const remW = dispatch.net_weight_kg;
  const perUnit = dispatch.quantity ? dispatch.weight_kg / dispatch.quantity : 0;
  const [type, setType] = useState("Partial Return");
  const [date, setDate] = useState(todayIso());
  const [qty, setQty] = useState("");
  const [weight, setWeight] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (type === "Full Return") { setQty(String(+remQ.toFixed(4))); setWeight(String(+remW.toFixed(4))); }
    else { setQty(""); setWeight(""); }
  }, [type, remQ, remW]);

  const onQty = (v) => { setQty(v); setWeight(v === "" ? "" : String(+(parseFloat(v || 0) * perUnit).toFixed(4))); };

  const save = async () => {
    const q = parseFloat(qty), w = parseFloat(weight);
    if (!date) return toast.error("Return Date is required");
    if (!(q > 0)) return toast.error("Returned Quantity must be greater than 0");
    if (q > remQ + 1e-6) return toast.error(`Returned Quantity cannot exceed remaining ${num(remQ, 2)}`);
    if (!(w > 0)) return toast.error("Returned Weight must be greater than 0");
    if (w > remW + 1e-6) return toast.error(`Returned Weight cannot exceed remaining ${kg(remW)}`);
    setBusy(true);
    try {
      const r = await api.post(`/dispatches/${dispatch.id}/returns`, { return_type: type, return_date: date, quantity: q, weight_kg: w, reason });
      toast.success(`Return saved · ${r.data.return_type}`);
      setReason("");
      onSaved(r.data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-3" data-testid="return-form">
      <div className="text-sm font-semibold text-slate-800">Record Return</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Return Type *</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="return-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Partial Return">Partial Return</SelectItem>
              <SelectItem value="Full Return">Full Return</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Return Date *</Label><DateDMY value={date} onChange={setDate} minDate={dispatch.date} testid="return-date" /></div>
        <div>
          <Label className="text-xs">Returned Quantity * <span className="text-slate-400">(max {num(remQ, 2)})</span></Label>
          <Input type="number" min="0" step="any" value={qty} onChange={(e) => onQty(e.target.value)} disabled={type === "Full Return"} data-testid="return-qty-input" />
        </div>
        <div>
          <Label className="text-xs">Returned Weight (Kg) * <span className="text-slate-400">(max {kg(remW)})</span></Label>
          <Input type="number" min="0" step="any" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="return-weight-input" />
        </div>
      </div>
      <div><Label className="text-xs">Reason for Return (optional)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="return-reason-input" /></div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="return-save-btn">{busy ? "Saving…" : "Save Return"}</Button>
      </div>
    </div>
  );
}

export default function ReturnsDialog({ dispatch, canEdit, onClose, onChanged }) {
  const [d, setD] = useState(dispatch);
  useEffect(() => { setD(dispatch); }, [dispatch]);

  const updated = (nd) => { setD(nd); onChanged(); };

  const cancel = async (r) => {
    const reason = window.prompt("Reason for cancelling this return (required):");
    if (!reason || !reason.trim()) return;
    try {
      const res = await api.post(`/dispatches/${d.id}/returns/${r.id}/cancel`, { reason });
      toast.success("Return cancelled");
      updated(res.data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const retained = d?.vehicle_type === "customer";
  return (
    <Dialog open={!!dispatch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="returns-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Undo2 className="h-4 w-4" /> Returns</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded-md">
              <b>{d.vehicle_number}</b> / {d.customer_name} / {d.invoice_number} · {d.product} {d.packing}
              <div className="text-xs mt-1">{d.transport_group} · Status: <b data-testid="returns-status">{d.consignment_status}</b></div>
            </div>
            <div className={`grid grid-cols-1 gap-2 ${d.dm_qty > 0 ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
              <Stat label="Gross Dispatched" qty={d.quantity} weight={d.weight_kg} testid="returns-gross" />
              {d.dm_qty > 0 && <Stat label={`POD ${d.pod}`} qty={d.dm_qty} weight={d.dm_weight_kg} testid="returns-dm" />}
              <Stat label="Total Returned" qty={d.returned_qty} weight={d.returned_weight_kg} testid="returns-total" />
              <Stat label={retained ? "Net Retained" : "Net"} qty={d.net_qty} weight={d.net_weight_kg} testid="returns-net" />
            </div>
            {canEdit && d.return_type !== "Full Return" && <ReturnForm dispatch={d} onSaved={updated} />}
            {!canEdit && <div className="text-xs text-slate-500">You can view returns but are not allowed to record or cancel them.</div>}
            <div>
              <div className="text-sm font-semibold text-slate-800 mb-2">Return History</div>
              <ReturnHistory returns={d.returns || []} canEdit={canEdit} onCancel={cancel} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
